import { Project, ProjectCreate, ProjectUpdate, ProjectUserInvite, ProjectUser, ProjectRole } from '../types/project';
import { API_CONFIG } from '../config';
import { api } from './api';
import { useAuth } from '../contexts/AuthContext';

interface UserDetails {
    id: number;
    email: string;
    first_name: string;
    last_name: string;
}

const projectService = {
    async getProjects(): Promise<Project[]> {
        try {
            console.log('Fetching all projects...');
            const response = await api.get('/projects');
            console.log('Projects fetched successfully:', response.data);
            return response.data;
        } catch (error: any) {
            console.error('Error fetching projects:', {
                message: error.message,
                status: error.response?.status,
                data: error.response?.data,
                config: error.config
            });
            
            if (error.response?.status === 401) {
                throw new Error('401 Unauthorized');
            }
            
            if (error.message === 'Network Error') {
                throw new Error('Failed to connect to the server. Please check your internet connection.');
            }
            
            throw new Error(error.response?.data?.detail || 'Failed to fetch projects');
        }
    },

    async getUserProjects(): Promise<Project[]> {
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                throw new Error('No authentication token found');
            }

            console.log('Fetching user projects...');
            const response = await api.get('/projects');
            console.log('User projects fetched successfully:', response.data);
            return response.data;
        } catch (error: any) {
            console.error('Error fetching user projects:', {
                message: error.message,
                status: error.response?.status,
                data: error.response?.data,
                config: error.config
            });
            
            if (error.response?.status === 401) {
                throw new Error('401 Unauthorized');
            }
            
            if (error.message === 'Network Error') {
                throw new Error('Failed to connect to the server. Please check your internet connection.');
            }
            
            throw new Error(error.response?.data?.detail || 'Failed to fetch user projects');
        }
    },

    async getProject(id: number): Promise<Project> {
        try {
            console.log(`Fetching project with ID ${id}...`);
            const response = await api.get(`/projects/${id}`);
            console.log('Project fetched successfully:', response.data);
            return response.data;
        } catch (error: any) {
            console.error(`Error fetching project ${id}:`, {
                message: error.message,
                status: error.response?.status,
                data: error.response?.data,
                config: error.config
            });
            
            if (error.response?.status === 404) {
                throw new Error('Project not found');
            }
            
            if (error.message === 'Network Error') {
                throw new Error('Failed to connect to the server. Please check your internet connection.');
            }
            
            throw new Error(error.response?.data?.detail || 'Failed to fetch project');
        }
    },

    async createProject(data: ProjectCreate): Promise<Project> {
        try {
            console.log('Creating new project:', data);
            const response = await api.post('/projects', data);
            console.log('Project created successfully:', response.data);
            return response.data;
        } catch (error: any) {
            console.error('Error creating project:', {
                message: error.message,
                status: error.response?.status,
                data: error.response?.data,
                config: error.config
            });
            
            if (error.response?.status === 400) {
                throw new Error(error.response.data?.detail || 'Invalid project data');
            }
            
            if (error.message === 'Network Error') {
                throw new Error('Failed to connect to the server. Please check your internet connection.');
            }
            
            throw new Error(error.response?.data?.detail || 'Failed to create project');
        }
    },

    async updateProject(id: number, data: FormData | ProjectUpdate): Promise<Project> {
        try {
            const headers = data instanceof FormData ? {
                'Content-Type': 'multipart/form-data'
            } : {
                'Content-Type': 'application/json'
            };

            console.log(`Updating project ${id}:`, data);
            const response = await api.put(`/projects/${id}`, data, { headers });
            console.log('Project updated successfully:', response.data);
            return response.data;
        } catch (error: any) {
            console.error(`Error updating project ${id}:`, {
                message: error.message,
                status: error.response?.status,
                data: error.response?.data,
                config: error.config
            });
            
            if (error.response?.status === 404) {
                throw new Error('Project not found');
            }
            
            if (error.response?.status === 400) {
                throw new Error(error.response.data?.detail || 'Invalid project data');
            }
            
            if (error.message === 'Network Error') {
                throw new Error('Failed to connect to the server. Please check your internet connection.');
            }
            
            throw new Error(error.response?.data?.detail || 'Failed to update project');
        }
    },

    async deleteProject(id: number): Promise<void> {
        try {
            console.log(`Deleting project ${id}...`);
            await api.delete(`/projects/${id}`);
            console.log('Project deleted successfully');
        } catch (error: any) {
            console.error(`Error deleting project ${id}:`, {
                message: error.message,
                status: error.response?.status,
                data: error.response?.data,
                config: error.config
            });
            
            if (error.response?.status === 404) {
                throw new Error('Project not found');
            }
            
            if (error.message === 'Network Error') {
                throw new Error('Failed to connect to the server. Please check your internet connection.');
            }
            
            throw new Error(error.response?.data?.detail || 'Failed to delete project');
        }
    },

    async getProjectUsers(projectId: number): Promise<ProjectUser[]> {
        try {
            console.log(`Fetching users for project ${projectId}...`);
            const response = await api.get(`/projects/${projectId}/users`);
            console.log('Project users fetched successfully:', response.data);
            return response.data;
        } catch (error: any) {
            console.error(`Error fetching users for project ${projectId}:`, {
                message: error.message,
                status: error.response?.status,
                data: error.response?.data,
                config: error.config
            });
            
            if (error.response?.status === 404) {
                throw new Error('Project not found');
            }
            
            if (error.message === 'Network Error') {
                throw new Error('Failed to connect to the server. Please check your internet connection.');
            }
            
            throw new Error(error.response?.data?.detail || 'Failed to fetch project users');
        }
    },

    async inviteUser(projectId: number, data: ProjectUserInvite): Promise<void> {
        try {
            console.log(`Inviting user to project ${projectId}:`, data);
            await api.post(`/projects/${projectId}/invite`, data);
            console.log('User invited successfully');
        } catch (error: any) {
            console.error(`Error inviting user to project ${projectId}:`, {
                message: error.message,
                status: error.response?.status,
                data: error.response?.data,
                config: error.config
            });
            
            if (error.response?.status === 404) {
                throw new Error('Project not found');
            }
            
            if (error.response?.status === 400) {
                throw new Error(error.response.data?.detail || 'Invalid invitation data');
            }
            
            if (error.message === 'Network Error') {
                throw new Error('Failed to connect to the server. Please check your internet connection.');
            }
            
            throw new Error(error.response?.data?.detail || 'Failed to invite user');
        }
    },

    async removeUser(projectId: number, userId: number): Promise<void> {
        try {
            console.log(`Removing user ${userId} from project ${projectId}...`);
            await api.delete(`/projects/${projectId}/users/${userId}`);
            console.log('User removed successfully');
        } catch (error: any) {
            console.error(`Error removing user ${userId} from project ${projectId}:`, {
                message: error.message,
                status: error.response?.status,
                data: error.response?.data,
                config: error.config
            });
            
            if (error.response?.status === 404) {
                throw new Error('Project or user not found');
            }
            
            if (error.message === 'Network Error') {
                throw new Error('Failed to connect to the server. Please check your internet connection.');
            }
            
            throw new Error(error.response?.data?.detail || 'Failed to remove user');
        }
    },

    async updateUserRole(projectId: number, userId: number, role: ProjectRole): Promise<ProjectUser> {
        try {
            console.log(`Updating role for user ${userId} in project ${projectId} to ${role}...`);
            const response = await api.put(`/projects/${projectId}/users/${userId}/role`, { role: role.toLowerCase() });
            console.log('User role updated successfully:', response.data);
            return response.data;
        } catch (error: any) {
            console.error(`Error updating role for user ${userId} in project ${projectId}:`, {
                message: error.message,
                status: error.response?.status,
                data: error.response?.data,
                config: error.config
            });
            
            if (error.response?.status === 404) {
                throw new Error('Project or user not found');
            }
            
            if (error.response?.status === 400) {
                throw new Error(error.response.data?.detail || 'Invalid role data');
            }
            
            if (error.message === 'Network Error') {
                throw new Error('Failed to connect to the server. Please check your internet connection.');
            }
            
            throw new Error(error.response?.data?.detail || 'Failed to update user role');
        }
    },

    async uploadImage(file: File, projectId: number): Promise<string> {
        try {
            console.log(`Uploading image for project ${projectId}...`);
            const formData = new FormData();
            formData.append('file', file);
            formData.append('project_id', projectId.toString());
            
            const response = await api.post(`/projects/${projectId}/upload-image`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });
            console.log('Image uploaded successfully:', response.data);
            return response.data.url;
        } catch (error: any) {
            console.error(`Error uploading image for project ${projectId}:`, {
                message: error.message,
                status: error.response?.status,
                data: error.response?.data,
                config: error.config
            });
            
            if (error.response?.status === 404) {
                throw new Error('Project not found');
            }
            
            if (error.response?.status === 400) {
                throw new Error(error.response.data?.detail || 'Invalid image data');
            }
            
            if (error.message === 'Network Error') {
                throw new Error('Failed to connect to the server. Please check your internet connection.');
            }
            
            throw new Error(error.response?.data?.detail || 'Failed to upload image');
        }
    },

    async getUserDetails(userId: number): Promise<UserDetails> {
        try {
            console.log(`Fetching details for user ${userId}...`);
            const response = await api.get(`/users/${userId}`);
            console.log('User details fetched successfully:', response.data);
            return response.data;
        } catch (error: any) {
            console.error(`Error fetching details for user ${userId}:`, {
                message: error.message,
                status: error.response?.status,
                data: error.response?.data,
                config: error.config
            });
            
            if (error.response?.status === 404) {
                throw new Error('User not found');
            }
            
            if (error.message === 'Network Error') {
                throw new Error('Failed to connect to the server. Please check your internet connection.');
            }
            
            throw new Error(error.response?.data?.detail || 'Failed to fetch user details');
        }
    }
};

export default projectService; 